import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable, tap } from 'rxjs';

import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { SessionProceedCoordinator } from '@app/services/session-proceed-coordinator.service';
import {
  buildDisruptionSignInRedirectExtras,
  orderBundleIncludesCancelForAnyReason,
} from '@app/utils/portal-sign-in-extras.util';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

import { Steps } from '@app/components/stepper/stepper.component';
import {
  AncillaryTypeEnum,
  AncillaryUpsellNode,
  PortalOrderNode,
} from '@app/services/api/api.types';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { StateService } from '@app/services/state/state.service';
import { PortalRoutes } from '@app/app-routing.module';
import { GetCoveragePipe } from '@app/pipes/get-coverage.pipe';
import { PortalCurrencyPipe } from '@app/pipes/portal-currency.pipe';
import { PopupsService } from '@voyagu/portal-design-system';

@Component({
  selector: 'disruption-protection-page',
  templateUrl: './disruption-protection-page.component.html',
  styleUrl: './disruption-protection-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class DisruptionProtectionPageComponent implements OnInit {
  Steps = Steps;

  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  stateService = inject(StateService);
  getCoveragePipe = inject(GetCoveragePipe);
  portalCurrencyPipe = inject(PortalCurrencyPipe);
  location = inject(Location);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  popupsService = inject(PopupsService);
  sessionProceed = inject(SessionProceedCoordinator);

  portalOrder$: Observable<PortalOrderNode | null>;
  portalOrder: PortalOrderNode | null;

  priceWithoutDiscountPerTraveler = 0;
  pricePerTraveler = 0;
  price = 0;

  coverage: number;

  sections: { title: string; items: { icon: string; label: string; details: string }[] }[] = [];

  ancillaryUpsell: boolean;

  isNewUser = false;

  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    Tracking.track(PortalTrackingEvents.portalDisruptionProtectionOpened);

    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
    ]).pipe(
      tap(([portalOrder, portalLead]) => {
        this.isNewUser = !!portalLead?.isNewUser;

        if (portalOrder) {
          this.portalOrder = portalOrder;

          const disruptionProtectionAncillaryBundle = portalOrder.ancillaryBundle?.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
          );

          const disruptionProtectionAncillary = portalOrder.ancillaries.find(
            (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
          );

          if (disruptionProtectionAncillaryBundle) {
            this.priceWithoutDiscountPerTraveler =
              disruptionProtectionAncillaryBundle.priceWithoutDiscountPerTraveler || 0;
            this.pricePerTraveler = disruptionProtectionAncillaryBundle.pricePerTraveler || 0;
            this.price = disruptionProtectionAncillaryBundle.priceWithoutDiscount || 0;
          } else if (disruptionProtectionAncillary) {
            this.priceWithoutDiscountPerTraveler =
              disruptionProtectionAncillary.amountPerTraveler || 0;
            this.pricePerTraveler = disruptionProtectionAncillary.amountPerTraveler || 0;
            this.price = disruptionProtectionAncillary.price || 0;
          }

          this.coverage = this.getCoveragePipe.transform(portalOrder.soldAmountPerAdult);

          this.sections = [
            {
              title: 'If your trip is disrupted, we will:',
              items: [
                {
                  icon: 'travel_green',
                  label: 'Rebook you fast',
                  details: `On the best available flight — up to 100% of your ticket + ${this.portalCurrencyPipe.transform(this.coverage)} covered`,
                },
                {
                  icon: 'money_green',
                  label: 'Refund you quickly',
                  details: 'Instant credit refund or cash refund in 7-10 days',
                },
                {
                  icon: 'support',
                  label: 'Support you anytime',
                  details: 'We handle your entire itinerary — even with multiple bookings, 24/7',
                },
                {
                  icon: 'thumbs_up',
                  label: 'No need to deal with airlines',
                  details: 'We handle changes, calls, and rebooking for you',
                },
              ],
            },
            {
              title: 'Without disruption protection, you may need:',
              items: [
                {
                  icon: 'travel_orange',
                  label: 'Miss an event or arrive later than planned',
                  details: "Airlines don't always offer the best alternatives",
                },
                {
                  icon: 'plane_orange',
                  label: 'Deal with airlines directly',
                  details: 'Long phone calls, chaotic service',
                },
                {
                  icon: 'travel_orange',
                  label: 'Wait weeks for refunds',
                  details: 'Airlines are slow in processing refunds',
                },
              ],
            },
          ];
        }
      }),
      map(([portalOrder]) => portalOrder),
    );
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.portalDisruptionProtectionBackPressed);
  }

  continue() {
    Tracking.track(PortalTrackingEvents.portalDisruptionProtectionContinuePressed);

    this.runAfterSessionCheck(() => {
      const disruptionProtection = Number(this.price) || 0;

      this.stateService.patchState({
        disruptionProtection,
        cancelForAnyReason: undefined,
        bundle: undefined,
      });

      this.redirect();
    });
  }

  next() {
    Tracking.track(PortalTrackingEvents.portalDisruptionProtectionNoThanksPressed);

    this.runAfterSessionCheck(() => {
      const disruptionProtection = undefined;

      this.stateService.patchState({
        disruptionProtection,
        cancelForAnyReason: undefined,
        bundle: undefined,
      });

      this.redirect();
    });
  }

  disruptionRedirectWouldTargetTravelers(): boolean {
    if (this.ancillaryUpsell) {
      return false;
    }

    return !orderBundleIncludesCancelForAnyReason(this.portalOrder);
  }

  runAfterSessionCheck(action: () => void) {
    this.sessionProceed.runAfterSessionCheckIf(
      this.disruptionRedirectWouldTargetTravelers(),
      this.isNewUser,
      action,
      () => this.showAskToSignInModal(),
    );
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  hideAskToSignInModal() {
    this.sessionProceed.dismissAskModal(() => this.popupsService.hideLast());
  }

  continueAsGuestFromSignInPrompt() {
    this.sessionProceed.continueAsGuestAfterPrompt(() => this.popupsService.hideLast());
  }

  showSignInModal() {
    const upsell = this.ancillaryUpsell
      ? (this.portalOrder as unknown as AncillaryUpsellNode)
      : null;

    const extras = buildDisruptionSignInRedirectExtras({
      state: this.stateService.getState(),
      portalOrder: this.portalOrder,
      disruptionPrice: Number(this.price) || 0,
      ancillaryUpsell: this.ancillaryUpsell,
      upsell,
      upsellTripPortalLink:
        this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam) ?? undefined,
    });

    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      extras,
    );
  }

  getSubmitNextSegment() {
    const afterBundleSkipsTravelers = this.ancillaryUpsell;

    if (this.portalOrder?.isChange) {
      return PortalRoutes.Gratuity;
    }

    if (afterBundleSkipsTravelers) {
      // The upsell journey never offers gratuity (TIC-7119).
      return PortalRoutes.OverviewPayment;
    }

    return PortalRoutes.Travelers;
  }

  redirect() {
    const hasCfarInBundle = orderBundleIncludesCancelForAnyReason(this.portalOrder);

    const nextSegment = this.getSubmitNextSegment();

    const path = this.ancillaryUpsell
      ? [
          PortalRoutes.AncillaryUpsell,
          this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam),
          hasCfarInBundle ? PortalRoutes.CancelForAnyReason : nextSegment,
        ]
      : [
          this.stateService.getState().portalLink,
          this.portalOrder?.portalLink,
          hasCfarInBundle ? PortalRoutes.CancelForAnyReason : nextSegment,
        ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }
}
